// src/components/ScheduleVisitModal.js
//
// Schedules a patient visit onto the shared **Research** Google Calendar directly
// from PatientDetailPage. Single-calendar design: the room is metadata (title
// prefix + location + a per-room event color), not a separate resource calendar.
//
// Flow: coordinator fills in room / times / study / visit / responsible coordinator,
// hits Save → we call the `upsertCalendarEvent` Cloud Function (reuses the Firebase
// Admin service account), get back { eventId, htmlLink }, and hand the whole
// appointment object back to the parent via onSaved(). The parent persists those
// fields on the normal patient Save (Firestore or the legacy Sheet). Passing an
// existing eventId reschedules the same event instead of creating a duplicate.

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarPlus, X, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { auth } from "../firebase";
import { fetchActiveCoordinators } from "../services/coordinatorService";
import SearchableSelect from "./SearchableSelect";
import {
  upsertCalendarEvent,
  deleteCalendarEvent,
} from "../services/researchCalendarService";
import { useCalendarRooms } from "./researchCalendarRooms";
import {
  useRoomBookingCheck,
  RoomCheckDialog,
  slotChanged,
} from "./roomBookingCheck";

// Rooms come from the site's calendar config (services/calendarConfigService.js).
// They used to be a second hardcoded copy of the list in researchCalendarRooms.js,
// which meant "Room 1..4" was baked into the product in two places.

/* Match PatientDetailPage's dark-glass input atoms. */
const inputCls =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition";
const inputDateCls = `${inputCls} [color-scheme:dark]`;
const labelCls =
  "block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1";
const selectCls =
  "w-full appearance-none px-3 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 cursor-pointer transition";

export default function ScheduleVisitModal({
  open,
  onClose,
  patientName = "",
  patientToken = "",
  appt = {}, // { date, startTime, endTime, room, otherRoom, coordinator, coordinatorEmail, study, visit, createdBy, eventId, eventLink }
  onSaved,
  onRemoved,
}) {
  const { rooms } = useCalendarRooms();
  const [coordinators, setCoordinators] = useState([]);
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [room, setRoom] = useState("");
  const [otherRoom, setOtherRoom] = useState("");
  const [coordinator, setCoordinator] = useState(""); // name (join key)
  const [study, setStudy] = useState("");
  const [visit, setVisit] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  // The shared "is this room actually free?" confirmation (roomBookingCheck.js),
  // the same one the Research Calendar page uses. There's no event list behind
  // this modal, so it relies entirely on the day it reads from the calendar —
  // which is the point: bookings made straight from Google Calendar are exactly
  // what a coordinator here can't otherwise see.
  const { requestCheck, dialogProps } = useRoomBookingCheck({
    open,
    resetKey: appt.eventId,
    date,
    startTime,
    endTime,
    room,
    otherRoom,
    excludeEventId: appt.eventId,
  });

  // Hydrate from the incoming appt each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setDate(appt.date || "");
    setStartTime(appt.startTime || "");
    setEndTime(appt.endTime || "");
    setRoom(appt.room || "");
    setOtherRoom(appt.otherRoom || "");
    setCoordinator(appt.coordinator || "");
    setStudy(appt.study || "");
    setVisit(appt.visit || "");
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fill in the site's first room once the list is known, without clobbering an
  // existing appointment's room or a choice the user just made. Separate from
  // the hydration effect because rooms resolve asynchronously.
  useEffect(() => {
    if (open && !room && rooms.length) setRoom(rooms[0].name);
  }, [open, room, rooms]);

  useEffect(() => {
    let alive = true;
    if (open) {
      fetchActiveCoordinators()
        .then((list) => alive && setCoordinators(list))
        .catch(() => alive && setCoordinators([]));
    }
    return () => {
      alive = false;
    };
  }, [open]);

  if (!open) return null;

  const coordinatorEmail =
    coordinators.find((c) => c.name === coordinator)?.email || "";

  const validate = () => {
    if (!date) return "Pick a date.";
    if (!startTime || !endTime) return "Pick a start and end time.";
    if (endTime <= startTime) return "End time must be after start time.";
    if (!room) return "Pick a room.";
    // Naming the room under "Other" is optional — plenty of visits genuinely
    // are just "somewhere else". Without a name RegX can't match it against
    // anything, which the room-check popup says outright rather than blocking.
    return "";
  };

  // Only the room and the slot decide whether a booking can clash, so editing
  // just the study or the coordinator on an existing visit doesn't re-ask.
  const needsRoomCheck =
    !appt.eventId ||
    slotChanged({ date, startTime, endTime, room, otherRoom }, appt);

  // Save is a two-step: the room-check confirmation first, then the write. The
  // Research calendar is shared and half of it is booked outside RegX, so this
  // asks someone to look before they book. It never refuses a booking.
  const handleSave = () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    if (needsRoomCheck) {
      requestCheck();
      return;
    }
    performSave();
  };

  const performSave = async () => {
    dialogProps.onCancel();
    setError("");
    setSaving(true);
    // "Created by" is immutable — stamp it once on the initial create, and on
    // every later reschedule just resend whatever it already was so it isn't
    // silently overwritten by whoever happens to make the next edit.
    const createdBy = appt.createdBy || auth.currentUser?.email || "unknown";
    try {
      const body = await upsertCalendarEvent({
        eventId: appt.eventId || "", // reschedule same event when present
        date,
        startTime,
        endTime,
        room,
        otherRoom,
        coordinatorName: coordinator,
        coordinatorEmail,
        study,
        visit,
        patientName,
        patientToken,
        createdBy,
      });
      onSaved?.({
        date,
        startTime,
        endTime,
        room,
        otherRoom,
        coordinator,
        coordinatorEmail,
        study,
        visit,
        createdBy,
        eventId: body.eventId,
        eventLink: body.htmlLink || "",
      });
      onClose?.();
    } catch (e) {
      setError(e.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!appt.eventId) {
      onRemoved?.();
      onClose?.();
      return;
    }
    setRemoving(true);
    setError("");
    try {
      await deleteCalendarEvent(appt.eventId);
      onRemoved?.();
      onClose?.();
    } catch (e) {
      setError(e.message || "Could not remove the event.");
    } finally {
      setRemoving(false);
    }
  };

  const busy = saving || removing;

  return (
    <>
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => !busy && !dialogProps.open && onClose?.()}
      >
        <motion.div
          className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0e1016]/95 shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 12 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-gradient-to-r from-purple-500/10 to-blue-500/10">
            <div className="flex items-center gap-2">
              <CalendarPlus className="w-4 h-4 text-purple-300" />
              <div>
                <h3 className="text-sm font-semibold text-white">
                  {appt.eventId ? "Reschedule Visit" : "Schedule Visit"}
                </h3>
                <p className="text-[11px] text-slate-400">
                  Research calendar · {patientName || "Patient"}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => !busy && onClose?.()}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <span className={labelCls}>Date</span>
                <input
                  type="date"
                  className={inputDateCls}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Start</span>
                <input
                  type="time"
                  className={inputDateCls}
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </label>
              <label className="block">
                <span className={labelCls}>End</span>
                <input
                  type="time"
                  className={inputDateCls}
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className={labelCls}>Room</span>
                <div className="relative">
                  <select
                    className={selectCls}
                    value={room}
                    onChange={(e) => setRoom(e.target.value)}
                  >
                    {rooms.map((r) => (
                      <option key={r.name} value={r.name} className="bg-[#0e1016]">
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
              {room === "Other" ? (
                <label className="block">
                  <span className={labelCls}>Which room? (optional)</span>
                  <input
                    className={inputCls}
                    value={otherRoom}
                    placeholder="e.g. Lab — or leave blank"
                    onChange={(e) => setOtherRoom(e.target.value)}
                  />
                </label>
              ) : (
                <label className="block">
                  <span className={labelCls}>Coordinator</span>
                  <div className="relative">
                    <SearchableSelect
                    value={coordinator}
                    options={coordinators.map((c) => c.name)}
                    onChange={setCoordinator}
                    clearOption={{ value: "", label: "— none —" }}
                    optionHint={(n) => (coordinators.find((c) => c.name === n)?.email ? "" : " (no email)")}
                    placeholder="Type to find a coordinator"
                    noMatchText="No active coordinator matches that."
                    inputClassName={`${selectCls} pl-9`}
                  />
                  </div>
                </label>
              )}
            </div>

            {room === "Other" && (
              <label className="block">
                <span className={labelCls}>Coordinator</span>
                <div className="relative">
                  <SearchableSelect
                    value={coordinator}
                    options={coordinators.map((c) => c.name)}
                    onChange={setCoordinator}
                    clearOption={{ value: "", label: "— none —" }}
                    optionHint={(n) => (coordinators.find((c) => c.name === n)?.email ? "" : " (no email)")}
                    placeholder="Type to find a coordinator"
                    noMatchText="No active coordinator matches that."
                    inputClassName={`${selectCls} pl-9`}
                  />
                </div>
              </label>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className={labelCls}>Study</span>
                <input
                  className={inputCls}
                  value={study}
                  placeholder="Study name"
                  onChange={(e) => setStudy(e.target.value)}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Visit</span>
                <input
                  className={inputCls}
                  value={visit}
                  placeholder="e.g. Screening, V2"
                  onChange={(e) => setVisit(e.target.value)}
                />
              </label>
            </div>

            {coordinator && !coordinatorEmail && (
              <p className="text-[11px] text-amber-300/90">
                {coordinator} has no email on file — they won't get a calendar
                invite. The visit still lands on the Research calendar.
              </p>
            )}

            {error && (
              <p className="text-[12px] text-rose-300 bg-rose-500/10 border border-rose-400/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            {appt.eventLink && (
              <a
                href={appt.eventLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[12px] text-purple-300 hover:text-purple-200"
              >
                View current event on Google Calendar
                <ExternalLink className="w-3 h-3" />
              </a>
            )}

            {appt.createdBy && (
              <p className="text-[10px] text-slate-500">
                Created by {appt.createdBy.split("@")[0]}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-white/10">
            {appt.eventId ? (
              <button
                type="button"
                onClick={handleRemove}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-rose-300 hover:text-white hover:bg-rose-500/20 border border-rose-400/20 transition disabled:opacity-50"
              >
                {removing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
                Remove
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => !busy && onClose?.()}
                disabled={busy}
                className="px-3 py-2 rounded-xl text-xs font-medium text-slate-300 hover:text-white hover:bg-white/10 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-purple-500 to-blue-500 shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)] hover:brightness-110 transition disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <CalendarPlus className="w-3.5 h-3.5" />
                )}
                {appt.eventId ? "Update event" : "Add to calendar"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>

    {/* Room double-booking check — the same confirmation the Research Calendar
        page shows, so a visit booked from a patient record gets the same look
        before it lands on the shared calendar. */}
    <RoomCheckDialog
      {...dialogProps}
      onConfirm={performSave}
      saving={saving}
      confirmLabel={appt.eventId ? "I've checked — reschedule" : "I've checked — book it"}
    />
    </>
  );
}
